import React from 'react';
import { CalendarDays, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

export type TravelMilestoneStep = 1 | 2;

type TravelMilestoneTrackerProps = {
  step: TravelMilestoneStep;
  durationDone: boolean;
  groupsDone: boolean;
  durationLabel: string;
  groupsLabel: string;
  onSelectStep?: (step: TravelMilestoneStep) => void;
};

/**
 * Compact 2-step tracker: 1 Duration → 2 Groups.
 */
export const TravelMilestoneTracker: React.FC<TravelMilestoneTrackerProps> = ({
  step,
  durationDone,
  groupsDone,
  durationLabel,
  groupsLabel,
  onSelectStep,
}) => {
  return (
    <ol className="flex w-full items-stretch gap-2 rounded-xl border border-[#3d2817]/10 bg-white/90 p-2">
      <li className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onSelectStep?.(1)}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
            step === 1 ? 'bg-[#2f9e44]/12 ring-1 ring-[#2f9e44]/35' : 'hover:bg-black/[0.03]'
          )}
        >
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
              durationDone
                ? 'bg-[#2f9e44] text-white'
                : step === 1
                  ? 'bg-[#3d2817] text-white'
                  : 'bg-[#3d2817]/10 text-[#3d2817]'
            )}
          >
            {durationDone ? '✓' : '1'}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#3d2817]/55">
              <CalendarDays className="h-3 w-3" aria-hidden />
              Step 1
            </span>
            <span className="block truncate text-xs font-semibold text-[#3d2817]">{durationLabel}</span>
          </span>
        </button>
      </li>
      <li className="flex w-4 shrink-0 items-center justify-center" aria-hidden>
        <span className="h-px w-full bg-[#3d2817]/15" />
      </li>
      <li className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onSelectStep?.(2)}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
            step === 2 ? 'bg-[#2f9e44]/12 ring-1 ring-[#2f9e44]/35' : 'hover:bg-black/[0.03]'
          )}
        >
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
              groupsDone
                ? 'bg-[#2f9e44] text-white'
                : step === 2
                  ? 'bg-[#3d2817] text-white'
                  : 'bg-[#3d2817]/10 text-[#3d2817]'
            )}
          >
            {groupsDone ? '✓' : '2'}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#3d2817]/55">
              <Users className="h-3 w-3" aria-hidden />
              Step 2
            </span>
            <span className="block truncate text-xs font-semibold text-[#3d2817]">{groupsLabel}</span>
          </span>
        </button>
      </li>
    </ol>
  );
};

export default TravelMilestoneTracker;
